import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { BarChart3, Code, ArrowRight } from "lucide-react";

export const Services = () => {
  const services = [
    {
      icon: <BarChart3 className="h-12 w-12 text-primary mb-4" />,
      title: "Consultoria Power BI",
      description: "Implementação, desenvolvimento e otimização de dashboards e relatórios personalizados para sua empresa",
      features: ["Dashboards Interativos", "Relatórios Automatizados", "Análise de KPIs", "Treinamento da Equipe"],
      link: "/consultoria"
    },
    {
      icon: <Code className="h-12 w-12 text-primary mb-4" />,
      title: "Power BI Embedded",
      description: "Integração de visualizações Power BI em suas aplicações com redução de até 50% em custos de licenciamento",
      features: ["Redução de Custos", "Portal Personalizado", "Gestão de Usuários", "API Integration"],
      link: "/embedded"
    }
  ];

  return (
    <section id="servicos" className="py-20 bg-muted/30">
      <div className="container mx-auto px-4 lg:px-8">
        <div className="text-center mb-16 animate-fade-in">
          <h2 className="text-3xl md:text-5xl font-bold mb-6">
            Nossos <span className="gradient-text">Serviços</span>
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Oferecemos soluções completas em Power BI, desde consultoria especializada até implementações customizadas
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
          {services.map((service, index) => (
            <Card 
              key={index} 
              className="group hover:shadow-lg transition-all duration-300 border-0 bg-gradient-card animate-slide-up"
              style={{ animationDelay: `${index * 200}ms` }}
            >
              <CardHeader className="text-center pb-4">
                <div className="flex justify-center">
                  {service.icon}
                </div>
                <CardTitle className="text-2xl mb-2">{service.title}</CardTitle>
                <CardDescription className="text-base leading-relaxed">
                  {service.description}
                </CardDescription>
              </CardHeader>
              
              <CardContent className="pt-4">
                <div className="space-y-3 mb-6">
                  {service.features.map((feature, featureIndex) => (
                    <div key={featureIndex} className="flex items-center text-sm">
                      <div className="w-2 h-2 bg-accent rounded-full mr-3"></div>
                      <span>{feature}</span>
                    </div>
                  ))}
                </div>
                
                <Button 
                  variant="outline" 
                  className="w-full group-hover:bg-primary group-hover:text-primary-foreground transition-colors duration-300"
                >
                  Saiba mais
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Portfolio Section */}
        <div className="mt-20">
          <div className="text-center mb-12">
            <h3 className="text-2xl md:text-3xl font-bold mb-4">
              Nosso <span className="gradient-text">Portfólio</span>
            </h3>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Confira alguns dos nossos projetos desenvolvidos em Power BI
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {[
              {
                title: "Comércio Eletrônico",
                url: "https://app.powerbi.com/view?r=eyJrIjoiYjczYzcyMmQtOGU2Yi00YmFiLWE3NjgtODdkMjY1NWM4MjRkIiwidCI6ImZlNzI5NWEzLTQ5ZDUtNDIxNy1iMzMzLWFkMDk5ZDQ2MmY2YiJ9",
                description: "Dashboard completo para análise de vendas online"
              },
              {
                title: "Redes Sociais",
                url: "https://app.powerbi.com/view?r=eyJrIjoiNTUwMzJkMjItOGE0OC00ODhkLThmOGItYjIzZjc0ZGUwMTBmIiwidCI6ImZlNzI5NWEzLTQ5ZDUtNDIxNy1iMzMzLWFkMDk5ZDQ2MmY2YiJ9",
                description: "Análise de engajamento e métricas sociais"
              },
              {
                title: "Financeiro",
                url: "https://app.powerbi.com/view?r=eyJrIjoiZWE5ODFjN2ItYmQ1Ni00MGRjLTg3Y2MtNmY2YjExMjVkZDYzIiwidCI6ImZlNzI5NWEzLTQ5ZDUtNDIxNy1iMzMzLWFkMDk5ZDQ2MmY2YiJ9",
                description: "Controle financeiro e análise de resultados"
              }
            ].map((project, index) => (
              <Card 
                key={index}
                className="group hover:shadow-xl transition-all duration-300 bg-gradient-card border-0"
              >
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg font-semibold">{project.title}</CardTitle>
                  <CardDescription className="text-sm">
                    {project.description}
                  </CardDescription>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="relative bg-muted rounded-lg overflow-hidden h-48 mb-4">
                    <iframe
                      src={project.url}
                      className="w-full h-full border-0 scale-75 origin-top-left"
                      style={{ 
                        width: '133.33%', 
                        height: '133.33%',
                        pointerEvents: 'none'
                      }}
                      title={project.title}
                    />
                    <div className="absolute inset-0 bg-transparent hover:bg-primary/5 transition-colors cursor-pointer flex items-center justify-center group-hover:backdrop-blur-[1px]">
                      <Button 
                        variant="secondary"
                        size="sm"
                        className="opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                        onClick={() => window.open(project.url, '_blank')}
                      >
                        Ver Projeto Completo
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* CTA Section */}
        <div className="text-center mt-16">
          <div className="bg-gradient-primary rounded-2xl p-8 md:p-12 text-white max-w-4xl mx-auto shadow-lg">
            <h3 className="text-2xl md:text-3xl font-bold mb-4">
              Pronto para começar seu projeto?
            </h3>
            <p className="text-lg mb-8 opacity-90">
              Entre em contato conosco e descubra como podemos transformar seus dados em resultados
            </p>
            <Button 
              size="lg" 
              className="bg-white text-primary hover:bg-gray-100 font-semibold px-8"
              onClick={() => {
                const element = document.getElementById('contato');
                if (element) element.scrollIntoView({ behavior: 'smooth' });
              }}
            >
              Solicitar Orçamento
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};